/**
 * Toast.jsx
 * Componente toast + provider para mostrar notificaciones sin usar alert().
 *
 * Uso:
 *   const { showToast } = useToast();
 *   showToast('Mensaje', { type: 'success', duration: 3000 });
 */
import React, { createContext, useContext, useCallback, useState, useRef } from 'react';
import styles from './Toast.module.css';

const ICONS = {
  success: '✓',
  error: '✕',
  warning: '⚠',
  info: 'ℹ',
};

const ToastContext = createContext(null);

/**
 * Referencia mutable compartida para casos donde no es posible usar
 * useToast() (por ejemplo, interceptores de Axios). Se asigna cuando
 * ToastProvider se monta. Si se llama antes de eso, la operación se ignora
 * silenciosamente.
 */
let globalShowToast = null;

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast debe usarse dentro de <ToastProvider>');
  }
  return ctx;
};

/**
 * Hook standalone para casos donde no es práctico usar useToast()
 * (por ejemplo, dentro de interceptores de Axios).
 * Devuelve null si el provider aún no se ha montado.
 */
export const getToastAPI = () => ({
  show: (message, options) => globalShowToast?.(message, options),
});

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const idRef = useRef(0);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.map(t => t.id === id ? { ...t, exiting: true } : t));
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 220);
  }, []);

  const showToast = useCallback((message, options = {}) => {
    const { type = 'info', duration = 3500 } = options;
    const id = ++idRef.current;
    const toast = { id, message, type, exiting: false };

    setToasts(prev => [...prev, toast]);

    if (duration > 0) {
      setTimeout(() => removeToast(id), duration);
    }
  }, [removeToast]);

  // Exponer globalmente para casos de uso fuera de componentes React
  globalShowToast = showToast;

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <div className={styles.container} role="region" aria-live="polite" aria-label="Notificaciones">
        {toasts.map(toast => (
          <div
            key={toast.id}
            className={`${styles.toast} ${styles[toast.type]} ${toast.exiting ? styles.exiting : ''}`}
            role="status"
          >
            <span className={styles.icon} aria-hidden="true">{ICONS[toast.type]}</span>
            <span className={styles.message}>{toast.message}</span>
            <button
              className={styles.close}
              onClick={() => removeToast(toast.id)}
              aria-label="Cerrar notificación"
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export default ToastProvider;
